import {LocationDescriptorObject} from 'history';
import {useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import {removeHashFromURL} from './urls';

// This is a temporary workaround to allow us to set the global history
// This should be removed once we have the time to update the function:
// handleNwHistoryNavigation in frontends/app/src/state/workspaces/url.ts
// trying to update that now will balloon the work required.
let duplicateGlobalHistory: ReturnType<typeof useHistory>;
export function getGlobalHistory(): ReturnType<typeof useHistory> {
  return duplicateGlobalHistory;
}
export function setGlobalHistory(history: ReturnType<typeof useHistory>): void {
  duplicateGlobalHistory = history;
}
// We must keep the canonical URL updated to the current route because
// certain third-party tools like Segment read it to get the current URL.
// See:
// https://weightsandbiases.slack.com/archives/CD72PQ03B/p1662679264760089
// https://github.com/wandb/core/pull/10732
export function useUpdateCanonicalURL() {
  const history = useHistory();
  useEffect(() => {
    let unregister: () => void;

    function updateCanonicalURL(): void {
      const currentURL = removeHashFromURL(window.location.href);

      const canonicalTag = document.querySelector('link[rel="canonical"]');
      canonicalTag?.setAttribute(`href`, currentURL);
    }

    if (!(window as any).__WB_BOT_MODE) {
      updateCanonicalURL();
      unregister = history.listen(updateCanonicalURL);
    }

    return () => {
      unregister?.();
    };
  }, [history]);
}

interface BaseNavigateOpts {
  replace?: boolean;
}

// Use this to navigate to a new path while optionally preserving search/hash
interface PathnameNavigateOpts extends BaseNavigateOpts {
  pathname: string;
  preserveSearch?: boolean;
  preserveHash?: boolean;
}

// Use this to navigate to a new location, including explicit new search/hash values
interface LocationNavigateOpts extends BaseNavigateOpts {
  location: LocationDescriptorObject;
}

type NavigateOpts = PathnameNavigateOpts | LocationNavigateOpts;

export function useNavigateTo() {
  const history = useHistory();
  return ({replace = false, ...opts}: NavigateOpts) => {
    if ('location' in opts) {
      if (replace) {
        history.replace(opts.location);
      } else {
        history.push(opts.location);
      }
      return;
    }

    if ('pathname' in opts) {
      const {pathname, preserveSearch = false, preserveHash = false} = opts;
      const {location} = history;

      const to: LocationDescriptorObject = {pathname};
      if (preserveSearch) {
        to.search = location.search;
      }
      if (preserveHash) {
        to.hash = location.hash;
      }

      if (replace) {
        history.replace(to);
      } else {
        history.push(to);
      }
      return;
    }
  };
}
